// This was generated during the migration to @kiwicom/eslint-config
// PLEASE REMOVE THE GLOBAL ESLINT DISABLE BELOW
// AND FIX ERRORS ACCORDINGLY.
/* eslint-disable multiline-comment-style */
import { includes, inc } from "ramda"

import type {
  CheckInAdditionalInfo,
  BoardingPass,
  CheckIn,
  CheckInStatus,
  PassengerTotals,
} from "../../../components/BookingCheckIn/types/internal"
import type {
  CheckInInbound,
  CheckInSegmentInbound,
  BoardingPassInbound,
} from "../../../components/BookingCheckIn/types/inbound"
import CHECKIN_STATUSES from "../../../components/BookingCheckIn/constants"

const {
  DONE,
  PROVIDE_DETAILS,
  PROVIDE_APAXI,
  WAITING_F0R_APAXI,
  WAITING_FOR_DETAILS,
  MISSED,
} = CHECKIN_STATUSES

const TOTAL_DEFAULTS: PassengerTotals = {
  totalPassengers: 0,
  totalBoardingPassesReady: 0,
  totalMissingDetails: 0,
}

const isBoardingPassReady = (status: CheckInStatus) => status === DONE
const isMissingDetails = (status: CheckInStatus) =>
  includes(status, [PROVIDE_DETAILS, PROVIDE_APAXI, WAITING_F0R_APAXI, WAITING_FOR_DETAILS, MISSED])

const reducePassengers = (
  { totalPassengers, totalBoardingPassesReady, totalMissingDetails }: PassengerTotals,
  {
    status,
  }: {
    status: CheckInStatus
  },
): PassengerTotals => ({
  totalPassengers: inc(totalPassengers),
  totalBoardingPassesReady: isBoardingPassReady(status)
    ? inc(totalBoardingPassesReady)
    : totalBoardingPassesReady,
  totalMissingDetails: isMissingDetails(status) ? inc(totalMissingDetails) : totalMissingDetails,
})

export const calculatePassengerStateTotals = ({
  passengers,
}: CheckInSegmentInbound): PassengerTotals => passengers.reduce(reducePassengers, TOTAL_DEFAULTS)

const mapperBoardingPasses = (boardingPasses?: BoardingPassInbound[] | null): BoardingPass[] =>
  boardingPasses
    ? boardingPasses.map((boardingPass) => ({
        fileName: boardingPass.file_name,
        fileUrl: boardingPass.file_url,
        type: boardingPass.type,
      }))
    : []

const mapperAdditionalInfo = (
  segment: CheckInSegmentInbound,
  segmentIdsWithHiddenCitiesInCombination: string[],
): CheckInAdditionalInfo => {
  /**
   * OTA - online travel agency - kiwi.com
   * @type {boolean}
   */
  const isInPersonCheckIn = segment.additional_info?.ota_blocking?.enabled ?? false
  const isPartOfTrueHiddenCityCombination = segmentIdsWithHiddenCitiesInCombination.includes(
    segment.segment_code,
  )

  if (!isInPersonCheckIn) {
    return {
      isPartOfTrueHiddenCityCombination,
    }
  }

  return {
    inPersonCheckIn: {
      minutesBeforeDeparture: segment.additional_info?.ota_blocking?.minutes_before_departure ?? 90,
    },
    isPartOfTrueHiddenCityCombination,
  }
}

const mapperCheckIn = (
  apiData: CheckInInbound,
  segmentIdsWithHiddenCitiesInCombination: string[],
): CheckIn | null | undefined => {
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!apiData) {
    return null
  }
  return {
    mergedBoardingPasses: mapperBoardingPasses(apiData.merged_boarding_passes),
    segments:
      // Fix this the next time the file is edited. (Flow to TS)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain
      apiData.segments &&
      apiData.segments.map((segment) => ({
        status: segment.status,
        passportDeadlineHoursBeforeDeparture:
          segment.passport_deadline_hours_before_departure || 24,
        boardingPassExpectedHoursBeforeDeparture:
          segment.boarding_pass_expected_hours_before_departure || 6,
        segmentCode: segment.segment_code,
        passengers:
          // Fix this the next time the file is edited. (Flow to TS)
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain
          segment.passengers &&
          segment.passengers.map((passenger) => ({
            id: passenger.passenger_id,
            status: passenger.status,
            boardingPasses: mapperBoardingPasses(passenger.boarding_passes),
            // airport fee not mapped as unused
          })),
        mergedBoardingPasses: mapperBoardingPasses(segment.merged_boarding_passes),
        /* $FlowFixMe This comment suppresses an error found when upgrading
         * Flow to v0.118.0. To view the error, delete this comment and run
         * Flow. */
        ...calculatePassengerStateTotals(segment),
        additionalInfo: mapperAdditionalInfo(segment, segmentIdsWithHiddenCitiesInCombination),
      })),
  }
}

export default mapperCheckIn
